import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from '@/store/hooks';
import { selectWorkingProjectDefaultLanguage } from '@/store/slices/project/selectors';
import { useEffect, useMemo } from 'react';
import { SETTINGS_TABS } from './settings-tab-config';
import useSetLayout from '@layouts/layout-grid/layout-utils/use-set-layout';
import useRevertPanelOnPathExit from '@layouts/layout-grid/layout-utils/use-revert-panel-on-path-exit';
import { EPanelDirections } from '@/store/slices/layout-grid/types';

import styles from './settings.module.css';
import { selectPanelOpen } from '@/store/slices/layout-grid/selectors';
import { getUser } from '@/store/slices/user/selectors';
import userHasRoleOnActiveChannel from '@/utils/user-has-role-on-active-channel';

const Settings = () => {
  useRevertPanelOnPathExit('/settings');
  useSetLayout({
    direction: EPanelDirections.LTR,
    panelOpen: true,
    oppositePanelOpen: false,
    footerOpen: false,
    pillarOpen: false,
    headerOpen: true,
  });
  const user = useAppSelector(getUser);
  const params = useParams();
  const activeTab = params['*'];
  const { projectUuid } = params;

  const navigate = useNavigate();
  const location = useLocation();

  const isPanelOpen = useAppSelector(selectPanelOpen);
  const defaultLanguage = useAppSelector(selectWorkingProjectDefaultLanguage);
  const language = defaultLanguage || 'en';

  const currentTab = SETTINGS_TABS.find((tab) => tab.value === activeTab);

  const navigableTabs = useMemo(() => {
    return SETTINGS_TABS.filter((tab) => {
      if (tab.roles) {
        return userHasRoleOnActiveChannel(tab.roles, user);
      }

      return true;
    });
  }, [user]);

  useEffect(() => {
    if ((!activeTab || !currentTab) && projectUuid) {
      const firstNavigable = navigableTabs[0];
      if (firstNavigable) {
        navigate(`/project/edit/${projectUuid}/${language}/settings/${firstNavigable.value}`, {
          state: location.state,
          replace: true,
        });
      } else {
        navigate(`/project/edit/${projectUuid}/${language}/`, { replace: true });
      }
    }
  }, [activeTab, currentTab, language, location.state, navigate, projectUuid, navigableTabs]);

  return (
    <div
      className={`flex-column ${styles['settings-container'] || ''} ${isPanelOpen ? '' : styles['settings-container-panel-closed'] || ''}`}
    >
      <Container className="flex-column gap-24" padding="0 var(--main-content-horizontal-padding)">
        <Container className="flex ai-center jc-space-between gap-16">
          <Text tag="h4" className="H4" design="GlobalIcoText1">
            Project Settings
            {currentTab ? (
              <Text tag="span" design="GlobalIcoText3">
                {' '}
                • {currentTab.label}
              </Text>
            ) : null}
          </Text>
          {currentTab?.headerAction}
        </Container>
        <Outlet />
      </Container>
    </div>
  );
};

export default Settings;
